import * as React from 'react';
import cx from 'clsx';
import { SkeletonLoader } from '@repo/design-system-kit';
import styles from './checkout-header.module.css';

export function CheckoutHeaderLoader(): React.JSX.Element {
  return (
    <div className={cx(styles.container, styles.loadingContainer)} data-test-checkout-header-loader>
      <div className={styles['loader-container']}>
        <SkeletonLoader.Line width="144px" />
      </div>
      <div className={styles['loader-container']}>
        <SkeletonLoader.Line width="48px" />
      </div>

      <div className={styles['loader-container']}>
        <SkeletonLoader.Line width="192px" />
      </div>
      <div className={styles['loader-container']}>
        <SkeletonLoader.Line width="48px" />
      </div>
    </div>
  );
}

interface CheckoutHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  topPart?: React.ReactNode;
  price?: React.ReactNode;
  priceBeforeDiscount?: React.ReactNode;
  priceBeforeDiscountDirection?: 'row' | 'column';
  priceNote?: React.ReactNode;
  priceCaption?: string;
  elevation?: 'high';
  withBorder?: boolean;
  variant?: 'primary' | 'primary-bundle' | 'secondary';
  layoutDirection?: 'row' | 'column';
}

export function CheckoutHeader({
  title,
  subtitle,
  price,
  priceBeforeDiscount,
  priceNote,
  priceCaption,
  elevation,
  withBorder,
  variant = 'primary',
  topPart,
  layoutDirection = 'row',
  priceBeforeDiscountDirection = 'column',
}: CheckoutHeaderProps): React.JSX.Element {
  const getTitleClassName = (): string => {
    switch (variant) {
      case 'primary-bundle':
        return cx('body-prominent', styles['primary-bundle-title']);
      case 'primary':
        return 'body-prominent';
      case 'secondary':
        return 'body-standard';
      default:
        return 'body-prominent';
    }
  };
  const getPriceClassName = (): string => {
    switch (variant) {
      case 'primary-bundle':
        return cx('body-prominent mb-4', styles['primary-bundle']);
      case 'primary':
        return 'body-prominent';
      case 'secondary':
        return 'body-standard';
      default:
        return 'body-prominent';
    }
  };

  return (
    <div
      className={cx(
        styles.container,
        elevation && styles['elevation-high'],
        withBorder && styles['with-border'],
        styles[variant],
        layoutDirection === 'column' && styles.vertical
      )}
    >
      {Boolean(topPart) && <div className={styles.topWrapper}>{topPart}</div>}
      <div
        className={cx(
          styles.innerWrapper,
          layoutDirection === 'column' && styles.vertical,
          priceCaption && styles.withCaption
        )}
      >
        <div className={styles.textWrapper}>
          <div className={getTitleClassName()} data-test-checkout-header-title>
            {title}
          </div>

          {Boolean(subtitle) && (
            <div
              className={
                variant === 'primary'
                  ? 'body-standard'
                  : cx('body-standard', styles['secondary-text'])
              }
              data-test-checkout-header-subtitle
            >
              {subtitle}
            </div>
          )}
        </div>

        {Boolean(priceCaption) && (
          <div
            className={cx('caption', styles['secondary-text'], styles.captionWrapper)}
            data-test-checkout-header-price-caption
          >
            {priceCaption}
          </div>
        )}

        <div className={styles.priceColumn}>
          <div
            className={cx(
              styles.priceAmount,
              priceBeforeDiscountDirection === 'row' && styles.priceAmountRow
            )}
          >
            <div className={getPriceClassName()} data-test-checkout-header-price>
              {price}
            </div>

            {priceBeforeDiscount ? (
              <div className={styles['price-discount']} data-test-checkout-header-price-discount>
                {priceBeforeDiscount}
              </div>
            ) : null}
          </div>

          {Boolean(priceNote) && (
            <div
              className={cx(styles['price-note'], 'caption')}
              data-test-checkout-header-price-note
            >
              {priceNote}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
